import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StyledButton } from "@insite/mobius/Clickable";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { LazyImage, Link, Typography } from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    title = "title",
    description = "description",
    link = "link",
    linkTitle = "linkTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        image: string;
        title: string;
        description: string;
        link: LinkFieldValue;
        linkTitle: string;
    };
}

type Props = OwnProps;

export interface ADINoSearchTipStyles {
    noSearchTipContainer?: InjectableCss;
    noSearchTipTextSection?: InjectableCss;
}

export const adiNoSearchTipStyles: ADINoSearchTipStyles = {
    noSearchTipContainer: {
        css: css`
        text-align: center; 
        background-color: ${ADIColors.gray6};
        margin-bottom: 20px;
        border-radius: 0px 0px 4px 4px;
        >${LazyImageStyle}{
            background-color: ${getColor("common.background")};
        }
        `
    },
    noSearchTipTextSection: {
        css: css`
        >h1{
            font-weight: 700;
            margin: 16px 0px 4px 0px;
            line-height: 1.25; 
            color: ${ADIColors.darkBlue1};
            font-size: 24px;
            @media only screen and (max-width: 1080px) {
                font-size: 20px;
            }
        }
        >p{
            color: ${getColor("text.main")};
            font-size: 14px;
            font-weight: normal;
            line-height: 1.6;
        }
        >div[class^="sc"] {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: center;
            padding: 8px 0px 24px 0px;
            >${StyledButton}{
                font-weight: 700;
                font-size: 14px;
                >${TypographyStyle}{
                    &:hover{
                        color: ${getColor("primary")};
                        text-decoration: underline;
                    }
                }
            }
        }
        `
    }
};

const styles = adiNoSearchTipStyles;

const ADINoSearchTip = ({ fields }: Props) => {
    const titleLink = useGetLink(fields.link);

    return (
        <StyledWrapper {...styles.noSearchTipContainer}>
            {fields?.image && (
                <LazyImage src={fields?.image} altText={fields?.title} />
            )}
            <StyledWrapper {...styles.noSearchTipTextSection}>
                {fields?.title && (
                    <Typography as="h1">{fields?.title}</Typography>
                )}
                {fields?.description && (
                    <Typography as="p">
                        {fields?.description}
                    </Typography>
                )}
                {fields?.link && (
                    <StyledWrapper>
                        <Link href={titleLink.url}>
                            {fields?.linkTitle}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ADINoSearchTip,
    definition: {
        group: "Common",
        displayName: "ADI No Search Tip",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.link,
                displayName: "Redirection Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Url"
                },
                sortOrder: 2,
            },
            {
                name: fields.linkTitle,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
